import type { ComponentType } from 'react'
import type { AuthSession } from '../features/auth/types'
import type { PermissionKey } from '../types/authorization'
import { permissions } from '../types/authorization'
import {
  getProtectedNavSections,
  resolveFirstAccessibleProtectedPath,
  resolvePreferredProtectedPath,
} from '../routes/protected'

type IconComponent = ComponentType<{ className?: string }>

export type NavItem = {
  key: string
  label: string
  path: string
  Icon: IconComponent
  requiresAny?: PermissionKey[]
  requiresAll?: PermissionKey[]
  requiresClient?: boolean
  visibleWhen?: {
    clientFlags?: Partial<{
      showAdvisoryMapping: boolean
      showProjectStatus: boolean
    }>
    envFlags?: Partial<{
      enableAdvisoryAIMenu: boolean
      enableBasMenu: boolean
    }>
    platformTypes?: number[]
  }
}

export type NavSection = {
  key: string
  label: string
  items: NavItem[]
}

type ProfileNavItem = {
  key: string
  label: string
  path: string
  requiresAny?: PermissionKey[]
}

const profileItems: ProfileNavItem[] = [
  { key: 'profile', label: 'My Profile', path: '/profile', requiresAny: [permissions.viewProfile] },
  {
    key: 'support',
    label: 'Support',
    path: '/support',
    requiresAny: [permissions.requestSupport],
  },
  {
    key: 'billing',
    label: 'Billing',
    path: '/billing',
    requiresAny: [permissions.viewBilling],
  },
  { key: 'logout', label: 'Logout', path: '/login/logout' },
]

function hasPermissionAccess(
  userPermissions: Set<PermissionKey>,
  requiresAny: PermissionKey[] = [],
  requiresAll: PermissionKey[] = []
) {
  const passesAny = requiresAny.length === 0 || requiresAny.some((perm) => userPermissions.has(perm))
  const passesAll = requiresAll.length === 0 || requiresAll.every((perm) => userPermissions.has(perm))
  return passesAny && passesAll
}

export function buildNavSections(
  session: AuthSession | null,
  userPermissions: Set<PermissionKey>,
  forceVisibleRouteKeys?: Set<string>
) {
  return getProtectedNavSections(session, userPermissions, forceVisibleRouteKeys)
}

export function resolveFirstAccessiblePath(
  session: AuthSession | null,
  userPermissions: Set<PermissionKey>
) {
  const firstSectionPath = resolveFirstAccessibleProtectedPath(session, userPermissions)
  if (firstSectionPath) return firstSectionPath

  const firstProfilePath = buildProfileMenu(session, userPermissions).find(
    (item) => item.path !== '/login/logout' && item.path !== '/support'
  )?.path

  return firstProfilePath || null
}

export function resolveHomePath(session: AuthSession | null, userPermissions: Set<PermissionKey>) {
  if (!session) return '/login'

  const preferredProtectedPath = resolvePreferredProtectedPath(session, userPermissions)
  if (preferredProtectedPath) return preferredProtectedPath

  const firstAccessiblePath = resolveFirstAccessiblePath(session, userPermissions)
  return firstAccessiblePath || '/profile'
}

export function buildProfileMenu(session: AuthSession | null, userPermissions: Set<PermissionKey>) {
  if (!session) return []
  return profileItems.filter((item) => hasPermissionAccess(userPermissions, item.requiresAny))
}
